/**
 * Copy for the Experian verification screen.
 *
 * PmcRegistrationController renders this same page as the *company*
 * registration — the Enterprise Flow — swapping only the heading, the submit
 * route, and companyName. That reuse is why the company step used to read like
 * a second run of the individual step: it showed the individual-verification
 * bullets and told the user their own identity was "verified separately".
 *
 * The company-branch strings are LOCKED copy, signed off with the Enterprise
 * Flow design. Reword them only against a new sign-off — verificationInstructions
 * .test.js asserts them verbatim precisely so an incidental edit fails loudly
 * rather than quietly shipping.
 *
 * It is not a second step. Experian's connectapi/v3/pmc endpoint takes one
 * business field (companyName) and otherwise identity-proofs a responsible
 * person, because knowledge-based authentication runs against a real
 * consumer credit file. Completing it issues the company token, and every
 * agent under that company inherits verification without verifying
 * individually — that is the entire point of the company-level model.
 *
 * companyName is the discriminator: only the company registration passes it.
 *
 * @param {{role?: string, companyName?: string|null}} context
 * @returns {{callout: string|null, bullets: string[]}}
 */
export function verificationInstructions({ role, companyName } = {}) {
    if (companyName) {
        return {
            callout: `You are registering the primary account holder for ${companyName} with Experian. This is a one-time, company-level verification. Once completed, every agent under ${companyName} can receive applicant reports without verifying themselves.`,
            bullets: [
                `Applicants share their reports with ${companyName}, and any agent under the company can view them. Your agents will not be asked to complete this step.`,
                'Experian verifies a company through a responsible person at that company, so the fields below must describe you personally (your name, home address, date of birth, and SSN).',
                'Do not enter business details anywhere on this form.',
                'You only have to do this once.',
            ],
        };
    }

    if (role === 'agent') {
        return {
            callout: null,
            bullets: [
                'In order for you to receive information on your applicants, you need to be verified first in our system.',
                'This is a verification of your identity only and must be completed in order for you to receive personal data from your rental applicants.',
                'Enter your own personal information only — do not enter any business information.',
            ],
        };
    }

    if (role === 'applicant') {
        return {
            callout: null,
            bullets: [
                'In order to begin your application process you need to be verified first in our system. This step does not begin your credit check.',
            ],
        };
    }

    return { callout: null, bullets: [] };
}
